import { useEffect, useState } from "react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { toast } from "sonner";
import { Stethoscope, Pill, ExternalLink, Check, ShieldCheck, Loader2, ArrowRight } from "lucide-react";

const CONCERNS = ["Menopause / perimenopause", "Hormone balance", "Metabolic health / weight", "Sleep", "Stress / mood", "GLP-1 program interest", "Other"];
const RX_INTERESTS = ["GLP-1 program consultation", "Hormone support", "Sleep support", "General supplement protocol", "Other"];

export default function Care() {
  const [intakes, setIntakes] = useState([]);
  const [bookingUrl, setBookingUrl] = useState("https://bloomher.health/book-consult");
  const [healthieConnected, setHealthieConnected] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [legupConnected, setLegupConnected] = useState(false);
  const [supplementsUrl, setSupplementsUrl] = useState("https://bloomher.health/supplements");

  const [form, setForm] = useState({ reason: "", primary_concern: "", goals: "", history: "", preferred_time: "" });
  const [submitting, setSubmitting] = useState(false);
  const [rx, setRx] = useState({ interest: "", notes: "" });
  const [submittingRx, setSubmittingRx] = useState(false);

  const load = () => {
    api.get("/care/intake").then((r) => { setIntakes(r.data.intakes); setBookingUrl(r.data.booking_url); setHealthieConnected(r.data.healthie_connected); }).catch(() => {});
    api.get("/rx/referral").then((r) => { setReferrals(r.data.referrals); setLegupConnected(r.data.leguprx_connected); setSupplementsUrl(r.data.supplements_url); }).catch(() => {});
  };
  useEffect(load, []);

  const submitIntake = async (e) => {
    e.preventDefault();
    if (!form.primary_concern) { toast.error("Please choose a primary concern"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/care/intake", form);
      toast.success("Intake submitted — let's get you to care.");
      load();
      window.open(data.booking_url, "_blank");
      setForm({ reason: "", primary_concern: "", goals: "", history: "", preferred_time: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  const submitRx = async (e) => {
    e.preventDefault();
    if (!rx.interest) { toast.error("Please choose an interest"); return; }
    setSubmittingRx(true);
    try {
      await api.post("/rx/referral", rx);
      toast.success("Referral submitted.");
      load();
      setRx({ interest: "", notes: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setSubmittingRx(false); }
  };

  return (
    <AppLayout>
      <div className="animate-fade-up">
        <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">Clinical Care</span>
        <h1 className="mt-2 font-serif text-5xl tracking-tight text-wine">When you need more support</h1>
        <p className="mt-3 text-base text-[#6E5862] max-w-2xl">
          Share a little about what you're experiencing and we'll hand you off securely to our HIPAA-compliant
          clinical partner platform for consults, intake, and provider care.
        </p>
      </div>

      <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#EAF0EC] px-4 py-2 text-xs font-medium text-[#4A6B53]">
        <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
        {healthieConnected ? "Connected — live sync enabled" : "Connection pending — your intake is saved & ready to sync"}
      </div>

      <div className="mt-8 rounded-[2rem] border border-taupe bg-white overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-8 pt-7 pb-4">
          <div>
            <h2 className="font-serif text-3xl text-[#3A1230]">Book your consult</h2>
            <p className="text-sm text-[#6E5862] mt-1">Schedule a 1:1 telehealth visit with your BloomHer practitioner.</p>
          </div>
          <a href={bookingUrl} target="_blank" rel="noreferrer" data-testid="care-open-scheduler"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-wine px-6 py-3 text-sm font-medium text-white hover:bg-wine-dark transition-all">
            Open scheduler <ExternalLink className="h-4 w-4" />
          </a>
        </div>
        <iframe
          data-testid="healthie-scheduler"
          src={bookingUrl}
          title="Book a consult with BloomHer"
          className="w-full"
          style={{ height: "720px", border: "0" }}
          allow="payment"
        />
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-8">
        {/* Intake form */}
        <div className="bg-white rounded-[2rem] border border-taupe p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-2xl bg-wine/10 flex items-center justify-center"><Stethoscope className="h-6 w-6 text-wine" strokeWidth={1.5} /></div>
            <h2 className="font-serif text-3xl text-[#3A1230]">Consult intake</h2>
          </div>
          <form onSubmit={submitIntake} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#6E5862] mb-2">What brings you in today?</label>
              <input data-testid="intake-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} required
                placeholder="In a sentence…" className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 text-base focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#6E5862] mb-2">Primary concern</label>
              <div className="flex flex-wrap gap-2">
                {CONCERNS.map((c) => (
                  <button type="button" key={c} data-testid={`intake-concern-${c.slice(0, 8).replace(/\W/g, "")}`} onClick={() => setForm({ ...form, primary_concern: c })}
                    className={`rounded-full px-4 py-2 text-sm transition-all ${form.primary_concern === c ? "bg-wine text-white" : "bg-[#F6ECF0] text-[#6E5862] hover:bg-taupe"}`}>{c}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#6E5862] mb-2">Your goals (optional)</label>
              <textarea data-testid="intake-goals" value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} rows={2}
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 text-base focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#6E5862] mb-2">Preferred time (optional)</label>
              <input data-testid="intake-time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                placeholder="e.g. Weekday mornings" className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 text-base focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine" />
            </div>
            <button data-testid="intake-submit" disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-full bg-wine px-6 py-3.5 text-sm font-medium text-white hover:bg-wine-dark disabled:opacity-60 transition-all">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Submit & book consult <ArrowRight className="h-4 w-4" /></>}
            </button>
            <p className="text-xs text-[#9B7E8D] text-center">You'll be handed off to our partner platform to complete booking.</p>
          </form>

          {intakes.length > 0 && (
            <div className="mt-6 border-t border-taupe pt-5">
              <p className="text-xs uppercase tracking-[0.1em] text-[#9B7E8D] mb-3">Your submissions</p>
              {intakes.map((it) => (
                <div key={it.id} data-testid={`intake-record-${it.id}`} className="flex items-center justify-between text-sm py-2">
                  <span className="text-[#3A1230]">{it.primary_concern}</span>
                  <span className="inline-flex items-center gap-1 text-[#4A6B53]"><Check className="h-3.5 w-3.5" /> {new Date(it.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Rx / supplements */}
        <div className="space-y-8">
          <div className="bg-white rounded-[2rem] border border-taupe p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-2xl bg-wine/10 flex items-center justify-center"><Pill className="h-6 w-6 text-wine" strokeWidth={1.5} /></div>
              <h2 className="font-serif text-3xl text-[#3A1230]">Pharmacy referral</h2>
            </div>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#F7E9EF] px-4 py-2 text-xs font-medium text-wine">
              {legupConnected ? "Connected to LegUp Rx" : "LegUp Rx pending — referral saved for handoff"}
            </div>
            <form onSubmit={submitRx} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#6E5862] mb-2">I'm interested in</label>
                <div className="flex flex-wrap gap-2">
                  {RX_INTERESTS.map((c) => (
                    <button type="button" key={c} data-testid={`rx-interest-${c.slice(0, 8).replace(/\W/g, "")}`} onClick={() => setRx({ ...rx, interest: c })}
                      className={`rounded-full px-4 py-2 text-sm transition-all ${rx.interest === c ? "bg-wine text-white" : "bg-[#F6ECF0] text-[#6E5862] hover:bg-taupe"}`}>{c}</button>
                  ))}
                </div>
              </div>
              <textarea data-testid="rx-notes" value={rx.notes} onChange={(e) => setRx({ ...rx, notes: e.target.value })} rows={2} placeholder="Notes (optional)"
                className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 text-base focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine resize-none" />
              <button data-testid="rx-submit" disabled={submittingRx} className="flex w-full items-center justify-center gap-2 rounded-full border border-wine px-6 py-3 text-sm font-medium text-wine hover:bg-wine/5 disabled:opacity-60 transition-all">
                {submittingRx ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request referral"}
              </button>
            </form>
          </div>

          <a href={supplementsUrl} target="_blank" rel="noreferrer" data-testid="care-shop-supplements"
            className="group flex items-center justify-between rounded-[2rem] bg-wine p-8 text-white transition-all hover:shadow-[0_20px_40px_rgba(107,37,84,0.25)]">
            <div>
              <h3 className="font-serif text-2xl">Shop supplements</h3>
              <p className="mt-1 text-sm text-white/80">Practitioner-grade protocols via Fullscript.</p>
            </div>
            <ExternalLink className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>

      <p className="mt-10 text-xs text-[#9B7E8D] leading-relaxed max-w-3xl">
        BloomHer does not provide diagnoses, prescriptions, or clinical treatment. All clinical care, including
        GLP-1 evaluation and prescriptions, is handled by licensed providers through our licensed partner platform and pharmacies.
      </p>
    </AppLayout>
  );
}
